import { Head, Link } from "@inertiajs/react";
import { Building2, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import AppLayout from "@/layouts/app-layout";
import { newOrganizationPath, organizationDashboardPath } from "@/routes";

interface Organization {
  id: number;
  name: string;
  slug: string;
  logo_url: string | null;
}

interface OrganizationsIndexProps {
  organizations: Organization[];
}

export default function OrganizationsIndex({ organizations }: OrganizationsIndexProps) {
  // No organizations - show waiting room
  if (organizations.length === 0) {
    return (
      <AppLayout>
        <Head title="Organizaciones" />
        <div className="flex h-full flex-1 flex-col items-center justify-center gap-6 p-6">
          <div className="text-center space-y-2">
            <Building2 className="h-16 w-16 mx-auto text-muted-foreground" />
            <h1 className="text-2xl font-bold">Bienvenido a Compralytics</h1>
            <p className="text-muted-foreground max-w-md">
              No perteneces a ninguna organizacion todavia. Crea una nueva o espera a que te inviten a una existente.
            </p>
          </div>
          <Button asChild size="lg">
            <Link href={newOrganizationPath()}>
              <Plus className="h-5 w-5 mr-2" />
              Crear Organizacion
            </Link>
          </Button>
        </div>
      </AppLayout>
    );
  }

  // Multiple organizations - show selector
  return (
    <AppLayout>
      <Head title="Seleccionar Organizacion" />
      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <div className="flex flex-col items-center text-center space-y-2">
          <h1 className="text-2xl font-bold">Selecciona una organizacion</h1>
          <p className="text-muted-foreground">
            Elige la organizacion con la que quieres trabajar
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 max-w-4xl mx-auto w-full">
          {organizations.map((org) => (
            <Link key={org.id} href={organizationDashboardPath(org.slug)}>
              <Card className="hover:border-primary transition-colors cursor-pointer h-full">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    {org.logo_url ? (
                      <img
                        src={org.logo_url}
                        alt={org.name}
                        className="h-10 w-10 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-primary" />
                      </div>
                    )}
                    <CardTitle className="text-lg">{org.name}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription>/{org.slug}</CardDescription>
                </CardContent>
              </Card>
            </Link>
          ))}

          <Link href={newOrganizationPath()}>
            <Card className="hover:border-primary transition-colors cursor-pointer h-full border-dashed">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                    <Plus className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <CardTitle className="text-lg text-muted-foreground">Nueva organizacion</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription>Crear una nueva organizacion</CardDescription>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </AppLayout>
  );
}
